import { ArrowRight, BedDouble, CheckCircle2, Heart, Mail, MessageSquareText, ShieldCheck, Sparkles } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Logo } from '../../components/Logo';
import { StatusNotice } from '../../components/StatusNotice';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../lib/api';

const localAccounts = [
  { label: 'Student', email: 'student@hostelgrid.local' },
  { label: 'Agency', email: 'agency@hostelgrid.local' },
  { label: 'NorthStar', email: 'northstar@hostelgrid.local' },
  { label: 'Admin', email: 'admin@hostelgrid.local' },
  { label: 'Advertiser', email: 'advertiser@hostelgrid.local' },
];

export function LoginPage() {
  const [params] = useSearchParams();
  const redirectTo = params.get('redirectTo') || '/';
  const agencyEntry = redirectTo.startsWith('/agency/onboarding');
  const { requestMagicLink, googleLogin, turnstileSiteKey, devLoginEnabled, devLogin, googleEnabled } = useAuth();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string>();
  const [developmentLink, setDevelopmentLink] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState('');
  const [devBusy, setDevBusy] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setStatus('sending'); setError('');
    try {
      const result = await requestMagicLink(email, redirectTo, token);
      setDevelopmentLink(result.developmentLink || '');
      setStatus('sent');
    } catch (cause) {
      setStatus('idle');
      setError(cause instanceof ApiError ? cause.message : 'Could not send the sign-in link.');
    }
  }

  async function useLocalAccount(accountEmail: string) {
    setDevBusy(accountEmail); setError('');
    try { await devLogin(accountEmail, redirectTo); }
    catch (cause) { setDevBusy(''); setError(cause instanceof Error ? cause.message : 'Could not open the local test account.'); }
  }

  return <div className="auth-page">
    <Link to="/" className="auth-brand"><Logo/><span>Back to discovery</span></Link>
    <section className="auth-story" aria-label="What a HostelGrid account unlocks">
      <span className="auth-story-kicker"><Sparkles size={15}/> One search. Clearer decisions.</span>
      <h2>Keep your hostel search in one trusted place.</h2>
      <p>Compare independent agencies, ask questions and track every important step without losing the hostel context.</p>
      <div className="auth-story-steps">
        <article><span><BedDouble/></span><div><strong>Compare real hostel options</strong><small>Room categories, facilities and recently confirmed availability.</small></div></article>
        <article><span><MessageSquareText/></span><div><strong>Keep enquiries organised</strong><small>Messages, viewings and placement updates stay together.</small></div></article>
        <article><span><Heart/></span><div><strong>Return to your shortlist</strong><small>Save promising hostels and continue when you are ready.</small></div></article>
      </div>
      <div className="auth-story-proof"><div className="auth-proof-avatars"><i>ST</i><i>HG</i><i>AG</i></div><span><strong>Built for accountable decisions</strong><small>Hostel facts, agency listings and reviews remain clearly separated.</small></span></div>
    </section>
    <div className="auth-card">
      <span className="eyebrow">{agencyEntry ? 'Agency registration' : 'Secure account access'}</span>
      <h1>{agencyEntry ? 'Create your agency account' : 'Continue to HostelGrid'}</h1>
      <p>{agencyEntry ? 'Enter your business email. A new HostelGrid account will be created automatically if you do not already have one, then you can register and verify your agency.' : 'Browse freely. Sign in only when you want to contact an agency, save a hostel, advertise or manage a workspace.'}</p>
      {params.get('error') && <StatusNotice type="error">The sign-in link was invalid or expired. Request a new one.</StatusNotice>}
      {error && <StatusNotice type="error">{error}</StatusNotice>}
      {status === 'sent' ? <div className="auth-success"><CheckCircle2/><h2>Check your email</h2><p>We sent a secure sign-in link to <strong>{email}</strong>. It expires in 15 minutes.</p>{developmentLink && <a className="primary-button full" href={developmentLink}>Open local sign-in link <ArrowRight size={17}/></a>}<button className="ghost-button" onClick={() => { setStatus('idle'); setDevelopmentLink(''); }}>Use another email</button></div> : <>
        {googleEnabled && <><button className="google-button" onClick={() => googleLogin(redirectTo)}><span className="google-mark">G</span>Continue with Google</button><div className="auth-divider"><span>or</span></div></>}
        <form onSubmit={submit} className="auth-form">
          <label><span>Email address</span><div className="input-shell"><Mail size={17}/><input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@example.com" required autoComplete="email"/></div></label>
          <TurnstileWidget siteKey={turnstileSiteKey} onToken={setToken}/>
          <button className="primary-button full" disabled={status === 'sending'}>{status === 'sending' ? 'Sending…' : agencyEntry ? 'Continue to agency registration' : 'Email me a sign-in link'} <ArrowRight size={17}/></button>
        </form>
      </>}
      {devLoginEnabled && <div className="local-login-panel"><div><span>Local testing</span><small>Open a seeded role without email delivery.</small></div><div>{localAccounts.map(account => <button key={account.email} type="button" disabled={Boolean(devBusy)} onClick={() => void useLocalAccount(account.email)}>{devBusy === account.email ? 'Opening…' : account.label}</button>)}</div></div>}
      <div className="assurance"><ShieldCheck size={17}/><span>HostelGrid never asks for your Google password and does not expose agency verification documents publicly.</span></div>
    </div>
  </div>;
}
